import React, { useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  Outlet,
  useSearchParams,
  useLocation,
  useParams,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { terminalGroupsQueryOptions } from './queries/terminalGroups';
import { CartProvider } from './context/CartContext';
import { useUserStore, selectIsSessionValid } from './store/userStore';
import { appPath, appLoginPath, resolveAfterLoginPath, APP_BASE } from './routes/appPaths';
import BottomNav from './components/BottomNav';
import { TerminalOfflineBanner } from './components/TerminalOfflineBanner';
import { SessionWatcher } from './components/SessionWatcher';
import { TelegramMiniApp } from './components/TelegramMiniApp';
import { YandexMetrika } from './components/YandexMetrika';
import OnboardingScreen from './screens/OnboardingScreen';
import HomeScreen from './screens/HomeScreen';
import ProductScreen from './screens/ProductScreen';
import EventDetailScreen from './screens/EventDetailScreen';
import CartScreen from './screens/CartScreen';
import EventsScreen from './screens/EventsScreen';
import SettingsScreen from './screens/SettingsScreen';
import OrdersHistoryScreen from './screens/OrdersHistoryScreen';
import CourierScreen from './screens/CourierScreen';
import CheckoutScreen from './screens/CheckoutScreen';
import PaymentReturnScreen from './screens/PaymentReturnScreen';
import PaymentWidgetScreen from './screens/PaymentWidgetScreen';
import PrivacyPolicyScreen from './screens/PrivacyPolicyScreen';
import DeleteAccountScreen from './screens/DeleteAccountScreen';
import DineInMenuScreen from './screens/DineInMenuScreen';
import DineInProductScreen from './screens/DineInProductScreen';
import TipOrderScreen from './screens/TipOrderScreen';
import HallPaymentReturnScreen from './screens/HallPaymentReturnScreen';
import PaymentTestScreen from './screens/PaymentTestScreen';
import PaymentFailedScreen from './screens/PaymentFailedScreen';
import LandingScreen from './screens/LandingScreen';
import LandingEventsScreen from './screens/LandingEventsScreen';
import LandingEventDetailScreen from './screens/LandingEventDetailScreen';
import LandingBookingScreen from './screens/LandingBookingScreen';
import VenueScreen from './screens/VenueScreen';
import { VENUES } from './venues';

function PrefetchTerminalGroups() {
  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.prefetchQuery(terminalGroupsQueryOptions());
  }, [queryClient]);
  return null;
}

function MainLayout() {
  useEffect(() => {
    document.documentElement.classList.add('app-shell');
    return () => document.documentElement.classList.remove('app-shell');
  }, []);

  return (
    <>
      <TerminalOfflineBanner />
      <Outlet />
      <BottomNav />
    </>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/** Только для экранов, где без аккаунта нельзя (оплата, история, checkout) */
function AuthGuard({
  children,
  allowHall = false,
}: {
  children: React.ReactNode;
  allowHall?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isAuthed = useUserStore(selectIsSessionValid);
  const clearExpiredSession = useUserStore((s) => s.clearExpiredSession);
  const hallOk = allowHall && Boolean(searchParams.get('hall'));

  useEffect(() => {
    if (hallOk) return;
    if (!isAuthed) {
      clearExpiredSession();
      navigate(appLoginPath(location.pathname + location.search), { replace: true });
    }
  }, [hallOk, isAuthed, clearExpiredSession, navigate, location.pathname, location.search]);

  if (hallOk) return <>{children}</>;
  if (!isAuthed) return null;
  return <>{children}</>;
}

function LoginRoute() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAuthed = useUserStore(selectIsSessionValid);
  const clearExpiredSession = useUserStore((s) => s.clearExpiredSession);
  const next = resolveAfterLoginPath(searchParams.get('next'));

  useEffect(() => {
    clearExpiredSession();
  }, [clearExpiredSession]);

  useEffect(() => {
    if (isAuthed) {
      navigate(next, { replace: true });
    }
  }, [isAuthed, navigate, next]);

  if (isAuthed) return null;
  return <OnboardingScreen />;
}

/** Старые URL приложения → /app/... */
function LegacyAppRedirect({ suffix }: { suffix: string }) {
  const location = useLocation();
  const target = `${appPath(suffix)}${location.search}${location.hash}`;
  return <Navigate to={target} replace />;
}

function LegacyProductRedirect() {
  const { id } = useParams();
  const location = useLocation();
  return <Navigate to={`${appPath(`/product/${id ?? ''}`)}${location.search}${location.hash}`} replace />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <PrefetchTerminalGroups />
      <CartProvider>
        <BrowserRouter>
          <YandexMetrika />
          <SessionWatcher />
          <TelegramMiniApp />
          <Routes>
            {/* Сайт */}
            <Route path="/" element={<LandingScreen />} />
            {VENUES.map((venue) => (
              <Route key={venue.slug} path={venue.path} element={<VenueScreen venue={venue} />} />
            ))}
            <Route path="/events" element={<LandingEventsScreen />} />
            <Route path="/events/:id" element={<LandingEventDetailScreen />} />
            <Route path="/booking" element={<LandingBookingScreen />} />
            <Route path="/loyalty" element={<Navigate to={appPath('/home')} replace />} />
            <Route path="/menu" element={<DineInMenuScreen />} />
            <Route path="/menu/product/:id" element={<DineInProductScreen />} />
            <Route path="/tip/:orderId" element={<TipOrderScreen />} />
            <Route path="/hall-payment-return" element={<HallPaymentReturnScreen />} />
            <Route path="/payment-return" element={<PaymentReturnScreen />} />
            <Route path="/payment-failed" element={<PaymentFailedScreen />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyScreen />} />
            <Route path="/delete-account" element={<DeleteAccountScreen />} />

            {/* Приложение под /app */}
            <Route path={APP_BASE}>
              <Route index element={<Navigate to={appPath('/home')} replace />} />
              <Route path="login" element={<LoginRoute />} />
              <Route
                path="orders"
                element={
                  <AuthGuard>
                    <OrdersHistoryScreen />
                  </AuthGuard>
                }
              />
              <Route
                path="payment-return"
                element={
                  <AuthGuard allowHall>
                    <PaymentReturnScreen />
                  </AuthGuard>
                }
              />
              <Route
                path="payment-widget"
                element={
                  <AuthGuard>
                    <PaymentWidgetScreen />
                  </AuthGuard>
                }
              />
              <Route
                path="payment-failed"
                element={
                  <AuthGuard allowHall>
                    <PaymentFailedScreen />
                  </AuthGuard>
                }
              />
              <Route path="payment-test" element={<PaymentTestScreen />} />
              <Route element={<MainLayout />}>
                <Route path="home" element={<HomeScreen />} />
                <Route
                  path="checkout"
                  element={
                    <AuthGuard>
                      <CheckoutScreen />
                    </AuthGuard>
                  }
                />
                <Route path="courier" element={<CourierScreen />} />
                <Route path="product/:id" element={<ProductScreen />} />
                <Route path="events" element={<EventsScreen />} />
                <Route path="events/:id" element={<EventDetailScreen />} />
                <Route path="cart" element={<CartScreen />} />
                <Route path="settings" element={<SettingsScreen />} />
              </Route>
            </Route>

            {/* Совместимость со старыми закладками */}
            <Route path="/login" element={<LegacyAppRedirect suffix="/login" />} />
            <Route path="/home" element={<LegacyAppRedirect suffix="/home" />} />
            <Route path="/cart" element={<LegacyAppRedirect suffix="/cart" />} />
            <Route path="/checkout" element={<LegacyAppRedirect suffix="/checkout" />} />
            <Route path="/settings" element={<LegacyAppRedirect suffix="/settings" />} />
            <Route path="/orders" element={<LegacyAppRedirect suffix="/orders" />} />
            <Route path="/courier" element={<LegacyAppRedirect suffix="/courier" />} />
            <Route path="/product/:id" element={<LegacyProductRedirect />} />
            <Route path="/payment-widget" element={<LegacyAppRedirect suffix="/payment-widget" />} />
            <Route path="/payment-test" element={<LegacyAppRedirect suffix="/payment-test" />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </CartProvider>
    </QueryClientProvider>
  );
}
